import {
  keepPreviousData,
  queryOptions,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { queryKeysContract } from '../queryKeys';

import { fetchContractArticleSuggestions } from './fetchContractArticleSuggestions';
import {
  type ContractArticleSuggestionsQueryParams,
  type ContractArticleSuggestionsResponse,
} from './types';

/**
 * Generates the query options for the contract article suggestions query so
 * they can be shared between the useQuery hook and prefetching.
 *
 * @param params - Optional typeahead query, party ids to boost, a contract to exclude from the history, and a result limit.
 * @param options - Additional options for the query.
 * @see https://app.dev.vestigas.com/redoc#tag/Contract-Suggestions/operation/get_contract_article_suggestions_contracts_suggestions_articles_get
 */
// fallow-ignore-next-line unused-export -- canonical query-options API for prefetching, no consumer yet per the tanstack-query-hooks skill
export const getContractArticleSuggestionsQueryOptions = <
  TData = ContractArticleSuggestionsResponse,
>({
  params = {},
  options,
}: {
  params?: Partial<ContractArticleSuggestionsQueryParams>;
  options?: Omit<
    UseQueryOptions<ContractArticleSuggestionsResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchContractArticleSuggestions(params, { signal }),
    queryKey: queryKeysContract.getArticleSuggestions(params),
    staleTime: ms('30s'),
    ...options,
  });

/**
 * React Query hook for fetching articles the caller's company has previously
 * used on contracts, to prefill a contract item row instead of retyping it.
 *
 * @param params - Optional typeahead query, party ids to boost, a contract to exclude from the history, and a result limit.
 * @param options - Additional options for the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Contract-Suggestions/operation/get_contract_article_suggestions_contracts_suggestions_articles_get
 */
export const useQueryContractArticleSuggestions = <
  TData = ContractArticleSuggestionsResponse,
>(
  params?: Partial<ContractArticleSuggestionsQueryParams>,
  options?: Omit<
    UseQueryOptions<ContractArticleSuggestionsResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getContractArticleSuggestionsQueryOptions<TData>({ options, params }),
  );
